import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["digestNow", "digestSummary", "digestHour", "digestUrgent", "digestZone", "digestBox",
                    "digestFinance", "digestFinanceOnly"]
  static values = { userId: Number }

  // A zone never saved starts on this browser's (Phelipe 2026-09-28): picked in
  // the list, or added to it when the list doesn't carry that exact zone.
  connect() {
    if (!this.hasDigestZoneTarget || this.digestZoneTarget.dataset.browserDefault !== "true") return
    const zone = this._browserZone()
    if (!zone) return
    const select = this.digestZoneTarget
    if (![...select.options].some(o => o.value === zone)) select.add(new Option(zone, zone), 0)
    select.value = zone
  }

  // SLC3-26 (SLCC, 2026-09-27) — emails right away, or one summary a day at the
  // hour picked, in the time zone picked (Phelipe 2026-09-28: 8 am to 8 pm, any
  // zone, and the Summary column's unticked rows keep coming right away).
  saveDigest() {
    const summary = this.hasDigestSummaryTarget && this.digestSummaryTarget.checked
    this.digestBoxTargets.forEach(box => { box.disabled = !summary })
    const skip = this.digestBoxTargets.filter(box => !box.checked).map(box => box.value)
    // Finances (Phelipe 2026-09-28): off, "also" in the summary, or - someone on an
    // Admin Assistant list - "only" in it for the copies they get as AA.
    const finance = this.hasDigestFinanceTarget && this.digestFinanceTarget.checked
    if (this.hasDigestFinanceTarget) this.digestFinanceTarget.disabled = !summary
    if (this.hasDigestFinanceOnlyTarget) {
      if (!finance) this.digestFinanceOnlyTarget.checked = false
      this.digestFinanceOnlyTarget.disabled = !summary || !finance
    }
    const financeOnly = this.hasDigestFinanceOnlyTarget && this.digestFinanceOnlyTarget.checked
    const body = new URLSearchParams({
      mode: summary ? "summary" : "now",
      hour: this.hasDigestHourTarget ? this.digestHourTarget.value : "18",
      urgent_now: String(this.hasDigestUrgentTarget ? this.digestUrgentTarget.checked : true),
      time_zone: (this.hasDigestZoneTarget && this.digestZoneTarget.value) || this._browserZone(),
      skip: skip.join(","),
      finances: finance ? (financeOnly ? "only" : "also") : "off"
    })
    this._post(`/users/${this.userIdValue}/update_email_digest`, body).then(() => this._showSaved()).catch(() => {})
  }

  _browserZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "" } catch (e) { return "" }
  }

  toggleReportType(event) {
    const btn = event.currentTarget
    const isActive = btn.getAttribute("aria-checked") === "true"
    const newState = !isActive
    const reportTypeId = btn.dataset.reportTypeId
    const whichArray = btn.dataset.whichArray

    this._flipToggle(btn, newState)

    const body = new URLSearchParams({
      report_type_id: reportTypeId,
      which_array: whichArray,
      checked: newState.toString()
    })

    this._post(`/users/${this.userIdValue}/update_notification_preferences`, body)
      .then(() => this._showSaved())
      .catch(() => this._flipToggle(btn, isActive))
  }

  togglePunchlist(event) {
    const btn = event.currentTarget
    const isActive = btn.getAttribute("aria-checked") === "true"
    const newState = !isActive
    const field = btn.dataset.field

    this._flipToggle(btn, newState)

    const body = new URLSearchParams({
      action_general: field,
      checked: newState.toString()
    })

    this._post(`/users/${this.userIdValue}/update_notification_preferences`, body)
      .then(() => this._showSaved())
      .catch(() => this._flipToggle(btn, isActive))
  }

  toggleFinance(event) {
    this._simpleToggle(event, "update_finance_aa_notifs_setting")
  }

  toggleDocuments(event) {
    this._simpleToggle(event, "update_documents_notifs_setting")
  }

  toggleCloseout(event) {
    this._simpleToggle(event, "update_closeout_documents_notifs_setting")
  }

  toggleDisableAll(event) {
    this._simpleToggle(event, "update_disable_notifs_setting")
  }

  // ── Private ──

  _simpleToggle(event, action) {
    const btn = event.currentTarget
    const isActive = btn.getAttribute("aria-checked") === "true"
    const newState = !isActive

    this._flipToggle(btn, newState)

    const body = new URLSearchParams({ checked: newState.toString() })

    this._post(`/users/${this.userIdValue}/${action}`, body)
      .then(() => this._showSaved())
      .catch(() => this._flipToggle(btn, isActive))
  }

  _flipToggle(btn, active) {
    btn.setAttribute("aria-checked", active.toString())
    const circle = btn.querySelector("span")

    if (active) {
      btn.classList.remove("bg-gray-200")
      // Use red for disable-all, brand for everything else
      const isDestructive = btn.dataset.action?.includes("toggleDisableAll")
      btn.classList.add(isDestructive ? "bg-red-500" : "bg-brand")
      circle.classList.remove("translate-x-1")
      circle.classList.add("translate-x-6")
    } else {
      btn.classList.remove("bg-brand", "bg-red-500")
      btn.classList.add("bg-gray-200")
      circle.classList.remove("translate-x-6")
      circle.classList.add("translate-x-1")
    }
  }

  _showSaved() {
    // Remove any existing toast so rapid toggles don't stack
    document.querySelectorAll(".flash-toast-notice[data-notif-toast]").forEach(el => el.remove())

    const html = `
      <div class="flash-toast-notice" data-controller="flash" data-flash-dismiss-after-value="2000" data-notif-toast role="alert">
        <div class="shrink-0">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p class="flex-1 text-sm">Saved</p>
      </div>
    `
    document.body.insertAdjacentHTML("afterbegin", html)
  }

  _post(url, body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken
      },
      body: body.toString()
    }).then(response => {
      if (!response.ok) throw new Error("Request failed")
      return response
    })
  }
}
